import React from "react";

const THEME = {
  primary: "#FB565F",
  primaryLight: "#fecdd3",
  success: "#10b981",
  successLight: "#d1fae5",
  info: "#3b82f6",
  infoLight: "#dbeafe",
  purple: "#8b5cf6",
  purpleLight: "#ede9fe",
};

/** Normalize stored labels (e.g. EURO → EUR). Any other non-empty code is kept for DB-driven currencies. */
const CURRENCY_ALIAS_MAP = {
  EURO: "EUR",
  EUR: "EUR",
  POUND: "GBP",
  POUNDS: "GBP",
  GBP: "GBP",
  PKR: "PKR",
  AED: "AED",
};

const DISPLAY_ORDER = ["PKR", "AED", "USD", "SAR", "EUR", "GBP", "INR", "KWD"];

export function normalizeCurrencyCode(currency) {
  const raw = (currency || "").toUpperCase().trim();
  if (!raw) return null;
  if (CURRENCY_ALIAS_MAP[raw]) return CURRENCY_ALIAS_MAP[raw];
  if (/^[A-Z]{2,4}$/.test(raw)) return raw;
  return null;
}

/** Aggregate by normalized currency code (only rows that normalize to a valid code). */
export function filterCurrencies(list) {
  if (!list || !Array.isArray(list)) return [];
  const byCode = {};
  list.forEach((c) => {
    const code = normalizeCurrencyCode(c.currency);
    if (!code) return;
    if (!byCode[code]) byCode[code] = { currency: code, amount: 0, count: 0 };
    byCode[code].amount += Number(c.amount) || 0;
    byCode[code].count += Number(c.count) || 0;
  });
  return Object.values(byCode).filter((c) => c.count > 0 || c.amount > 0);
}

/** Sort for display; no zero-padding — only currencies present in data. */
export function sortCurrenciesForDisplay(list) {
  if (!list || !list.length) return [];
  return [...list].sort((a, b) => {
    const ca = (a.currency || "").toUpperCase();
    const cb = (b.currency || "").toUpperCase();
    const ia = DISPLAY_ORDER.indexOf(ca);
    const ib = DISPLAY_ORDER.indexOf(cb);
    if (ia !== -1 || ib !== -1) {
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    }
    return ca.localeCompare(cb);
  });
}

/** Union of currency codes present in any of the raw API rows (no amount aggregation). */
export function uniqueCurrencyCodesFromRows(...rowArrays) {
  const seen = new Set();
  rowArrays.forEach((rows) => {
    (rows || []).forEach((c) => {
      const code = normalizeCurrencyCode(c.currency);
      if (code) seen.add(code);
    });
  });
  return sortCurrenciesForDisplay([...seen].map((currency) => ({ currency, amount: 0, count: 1 }))).map((x) => x.currency);
}

function paymentCurrenciesFromApi(rawList) {
  return sortCurrenciesForDisplay(filterCurrencies(rawList));
}

/** @deprecated Use paymentCurrenciesFromApi — kept for compatibility; no longer pads missing currencies. */
export function ensureAllCurrencies(list) {
  return sortCurrenciesForDisplay(filterCurrencies(list));
}

/** Currency symbol for card icon – PKR uses Pakistani Rupee sign (₨), not Indian (₹). */
export function getCurrencySymbol(currency) {
  const code = (currency || "PKR").toUpperCase();
  const map = {
    PKR: "₨", // Pakistani Rupee (U+20A8)
    INR: "₹", // Indian Rupee
    AED: "د.إ",
    SAR: "﷼",
    USD: "$",
    EUR: "€",
    GBP: "£",
    KWD: "KD",
  };
  return map[code] || code;
}

function formatAmount(amount, currency = "PKR") {
  const num = Number(amount);
  if (isNaN(num)) return "—";
  const code = normalizeCurrencyCode(currency) || (currency || "PKR").toUpperCase();
  if (["AED", "EUR", "GBP", "USD", "SAR", "INR", "KWD"].includes(code)) {
    return `${code} ${num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  return `Rs. ${num.toLocaleString("en-PK", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/** Single-line display for "by currency" sections: code once, then symbol + amount + count (no duplication). */
export function formatCurrencyLine(c) {
  const code = (c?.currency || "PKR").toUpperCase().trim();
  const symbol = getCurrencySymbol(code);
  const amount = Number(c?.amount) ?? 0;
  const count = Number(c?.count) ?? 0;
  const amountStr = amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return `${code}: ${symbol} ${amountStr} (${count} payment${count !== 1 ? "s" : ""})`;
}

const PaymentStatCard = ({ label, count, amountDisplay, amountItems, gradientFrom, gradientTo, iconBg, borderColor, currencySymbol }) => (
  <div
    className="relative overflow-hidden rounded-2xl border-2 p-4 sm:p-5 transition-all duration-200 hover:shadow-lg"
    style={{
      borderColor: borderColor || THEME.primaryLight,
      background: `linear-gradient(135deg, #ffffff 0%, ${gradientFrom || THEME.primaryLight}20 100%)`,
    }}>
    <div className="relative z-10">
      <div
        className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3"
        style={{ backgroundColor: iconBg || THEME.primaryLight }}>
        <span className="text-lg font-bold" style={{ color: gradientTo || THEME.primary }}>
          {currencySymbol}
        </span>
      </div>
      <p className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">{label}</p>
      {/* Main: amount – single line or multi-currency pills */}
      {amountItems && amountItems.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-2">
          {amountItems.map((item) => (
            <span
              key={item.key}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-sm font-bold text-gray-800 border border-gray-200/80 bg-white/90 shadow-sm"
            >
              <span className="text-gray-500 font-semibold">{item.symbol}</span>
              <span>{item.text}</span>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xl sm:text-2xl lg:text-3xl font-extrabold mt-1 text-gray-900 break-words">{amountDisplay ?? "—"}</p>
      )}
      {/* Below: number of payments */}
      <p className="text-sm font-medium mt-1.5 text-gray-500">
        {count ?? 0} payment{(count ?? 0) !== 1 ? "s" : ""}
      </p>
    </div>
    <div
      className="absolute bottom-0 right-0 w-20 h-20 rounded-full opacity-15 -mr-6 -mb-6"
      style={{ backgroundColor: gradientTo || THEME.primary }}
    />
  </div>
);

export default function DashboardPaymentCards({ paymentAnalytics, selectedCurrency }) {
  const today = paymentAnalytics?.today ?? {};
  const month = paymentAnalytics?.month ?? {};
  const total = paymentAnalytics?.total ?? {};
  const rawByCurrency = paymentAnalytics?.by_currency ?? [];
  const rawTodayByCurrency = (paymentAnalytics?.today ?? {}).by_currency ?? [];
  const rawMonthByCurrency = (paymentAnalytics?.month ?? {}).by_currency ?? [];

  const byCurrency = paymentCurrenciesFromApi(rawByCurrency);
  const todayByCurrency = paymentCurrenciesFromApi(rawTodayByCurrency);
  const monthByCurrency = paymentCurrenciesFromApi(rawMonthByCurrency);

  const effectiveCurrency =
    selectedCurrency ||
    (byCurrency.length === 1 ? byCurrency[0].currency : null) ||
    (byCurrency[0]?.currency ?? "PKR");
  const currencySymbol = getCurrencySymbol(effectiveCurrency);

  const showTotalByCurrency = byCurrency.length > 0;
  const totalByCurrencyLabel = showTotalByCurrency
    ? `Total by currency (${byCurrency.map((c) => c.currency).join(", ")})`
    : "Total by currency";

  /** Build amount display: either single amount string or list of { key, symbol, text } for multi-currency pills */
  const getAmountDisplay = (amount, currencies) => {
    if (currencies && currencies.length > 1) {
      return {
        items: currencies.map((c) => {
          const full = formatAmount(c.amount, c.currency);
          const symbol = getCurrencySymbol(c.currency);
          const text = full.replace(/^[^\d,.-]+/, "").trim() || String(c.amount);
          return { key: c.currency, symbol, text };
        }),
        single: null,
      };
    }
    if (currencies && currencies.length === 1) {
      const c = currencies[0];
      return { items: null, single: formatAmount(c.amount, c.currency) };
    }
    return { items: null, single: formatAmount(amount, effectiveCurrency) };
  };

  return (
    <div className="mb-0">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-5">
        <PaymentStatCard
          label="Today"
          count={today.count}
          amount={today.amount}
          amountDisplay={getAmountDisplay(today.amount, todayByCurrency).single}
          amountItems={getAmountDisplay(today.amount, todayByCurrency).items}
          gradientFrom={THEME.infoLight}
          gradientTo={THEME.info}
          iconBg={THEME.infoLight}
          borderColor="#bfdbfe"
          currencySymbol={currencySymbol}
        />
        <PaymentStatCard
          label="This Month"
          count={month.count}
          amount={month.amount}
          amountDisplay={getAmountDisplay(month.amount, monthByCurrency).single}
          amountItems={getAmountDisplay(month.amount, monthByCurrency).items}
          gradientFrom={THEME.primaryLight}
          gradientTo={THEME.primary}
          iconBg={THEME.primaryLight}
          borderColor="#fecdd3"
          currencySymbol={currencySymbol}
        />
        <PaymentStatCard
          label="Total"
          count={total.count}
          amount={total.amount}
          amountDisplay={getAmountDisplay(total.amount, byCurrency).single ?? (byCurrency.length === 1 ? formatAmount(byCurrency[0].amount, byCurrency[0].currency) : formatAmount(total.amount, effectiveCurrency))}
          amountItems={getAmountDisplay(total.amount, byCurrency).items}
          gradientFrom={THEME.purpleLight}
          gradientTo={THEME.purple}
          iconBg={THEME.purpleLight}
          borderColor="#ddd6fe"
          currencySymbol={currencySymbol}
        />
      </div>
      {showTotalByCurrency && (
        <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200">
          <p className="text-sm font-semibold text-gray-700 mb-2">{totalByCurrencyLabel}</p>
          <div className="flex flex-wrap gap-3">
            {byCurrency.map((c) => (
              <span key={c.currency} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-800">
                {formatCurrencyLine(c)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { formatAmount };
